import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import type { IReglamento, IReglamentoForm } from 'models/reglamento_model';
import {
  getReglamentosService,
  createReglamentoService,
  editReglamentoService,
  deleteReglamentoService,
  downloadFileReglamentoService,
} from 'repositories/reglamento_repository';

interface ReglamentoState {
  reglamentos: IReglamento[];
  count: number;
  loading: boolean;
  error: string | null;
}

const initialState: ReglamentoState = {
  reglamentos: [],
  count: 0,
  loading: false,
  error: null,
};

export const cargarReglamentos = createAsyncThunk(
  'reglamento/cargarReglamentos',
  async (
    params: { limit: number; page: number; nombreFilter?: string },
    { rejectWithValue },
  ) => {
    try {
      const { rows, count } = await getReglamentosService({
        nombre: params.nombreFilter,
        limit: params.limit,
        page: params.page,
      });
      return { rows: rows ?? [], count: count ?? 0 };
    } catch {
      return rejectWithValue('Error al cargar reglamentos');
    }
  },
);

export const addReglamento = createAsyncThunk(
  'reglamento/addReglamento',
  async (data: IReglamentoForm, { rejectWithValue }) => {
    try {
      await createReglamentoService(data);
    } catch {
      return rejectWithValue('Error al crear reglamento');
    }
  },
);

export const editReglamento = createAsyncThunk(
  'reglamento/editReglamento',
  async ({ data, userId }: { data: IReglamentoForm; userId: string }, { rejectWithValue }) => {
    try {
      await editReglamentoService(data, userId);
    } catch {
      return rejectWithValue('Error al editar reglamento');
    }
  },
);

export const deleteReglamento = createAsyncThunk(
  'reglamento/deleteReglamento',
  async (id: string, { rejectWithValue }) => {
    try {
      await deleteReglamentoService(id);
    } catch {
      return rejectWithValue('Error al eliminar reglamento');
    }
  },
);

export const downloadFileReglamento = createAsyncThunk(
  'reglamento/downloadFile',
  async ({ id, fileName }: { id: string; fileName: string }, { rejectWithValue }) => {
    try {
      await downloadFileReglamentoService(id, fileName);
    } catch {
      return rejectWithValue('Error al descargar archivo');
    }
  },
);

export const reglamentoSlice = createSlice({
  name: 'reglamento',
  initialState,
  reducers: {
    setReglamentos: (state, action: PayloadAction<{ rows: IReglamento[]; count: number }>) => {
      state.reglamentos = action.payload.rows;
      state.count = action.payload.count;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(cargarReglamentos.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cargarReglamentos.fulfilled, (state, action) => {
        state.loading = false;
        state.reglamentos = action.payload.rows;
        state.count = action.payload.count;
      })
      .addCase(cargarReglamentos.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setReglamentos } = reglamentoSlice.actions;
export default reglamentoSlice.reducer;
